import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs, { type Dayjs } from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { lessonsApi } from '@/api/lessons'
import type { Lesson } from '@/types'
import { getErrorMessage } from '@/utils/error'

type LessonRescheduleDialogProps = {
  open: boolean
  lesson: Lesson | null
  onClose: () => void
  onSuccess: () => void
}

export function LessonRescheduleDialog({ open, lesson, onClose, onSuccess }: LessonRescheduleDialogProps) {
  const { t } = useTranslation()
  const [rescheduleStart, setRescheduleStart] = useState<Dayjs | null>(null)
  const [rescheduleEnd, setRescheduleEnd] = useState<Dayjs | null>(null)
  const [rescheduleReason, setRescheduleReason] = useState('')
  const [rescheduleError, setRescheduleError] = useState<string | null>(null)
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false)

  useEffect(() => {
    if (!open || !lesson?.planned_start_at || !lesson.planned_end_at) {
      return
    }
    setRescheduleStart(dayjs(lesson.planned_start_at))
    setRescheduleEnd(dayjs(lesson.planned_end_at))
    setRescheduleReason('')
    setRescheduleError(null)
  }, [open, lesson?.id, lesson?.planned_start_at, lesson?.planned_end_at])

  const handleReschedule = async () => {
    if (!lesson || !rescheduleStart || !rescheduleEnd) return
    setRescheduleSubmitting(true)
    setRescheduleError(null)
    try {
      await lessonsApi.reschedule(lesson.id, {
        planned_start_at: rescheduleStart.toISOString(),
        planned_end_at: rescheduleEnd.toISOString(),
        reason: rescheduleReason.trim() || null,
      })
      onClose()
      onSuccess()
    } catch (e: unknown) {
      setRescheduleError(getErrorMessage(e) || t('lessons.rescheduleError'))
    } finally {
      setRescheduleSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('lessons.reschedule.title', 'Перенос урока')}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {rescheduleError && <Alert severity="error">{rescheduleError}</Alert>}
            <DateTimePicker
              label={t('lessons.reschedule.startAt', t('lessons.plannedStart', 'Начало'))}
              value={rescheduleStart}
              onChange={(value) => setRescheduleStart(value)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <DateTimePicker
              label={t('lessons.reschedule.endAt', t('lessons.plannedEnd', 'Окончание'))}
              value={rescheduleEnd}
              onChange={(value) => setRescheduleEnd(value)}
              minDateTime={rescheduleStart ?? undefined}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <TextField
              label={t('lessons.reschedule.reason', t('lessons.rescheduleReason', 'Причина'))}
              value={rescheduleReason}
              onChange={(e) => setRescheduleReason(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => void handleReschedule()}
          disabled={rescheduleSubmitting || !rescheduleStart || !rescheduleEnd}
        >
          {rescheduleSubmitting ? t('common.loading') : t('lessons.reschedule.submit', 'Перенести')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
